import i18n from 'invision-core/src/components/i18n/i18n';
import { getFilterService } from 'invision-core/src/components/injectables/injector.helper';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeLoadedSelector, MetadataCodeTypeDictionarySelector, MetadataCodeTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { RouteParams } from 'invision-core/src/components/router/router.selectors';
import { CurrentBusinessUnitTimeZoneOffsetSelector } from 'invision-core/src/components/session/businessunit.selectors';
import { PageSizePreferenceSelector } from 'invision-core/src/components/session/session.selectors';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { convertUnitsUserFriendly, convertUnitsUserFriendlyIntoSmallerDescriptiveUnits, getAllUnitsInTheUnitCategory } from 'invision-core/src/utilities/unitConversion/convert';
import moment from 'moment';
import { pathOr } from 'ramda';
import { createSelector } from 'reselect';
import { BYTE_UNIT_CODE, SERVICE_ENTITLEMENTS_ACTIVATION_STATUS, UNIT_OF_MEASURES, USAGE_CALL_DIRECTION, USAGE_DETAILS_ACTION_TYPES } from '../../customercare.constants';
import localeKeys from './../../locales/keys';
import { ConvergentBillerAccountUsageDetailsByServiceIdSelector, CurrentAccountCurrencyCodeSelector, CurrentAccountSummarySelector, IsFetchingAccountUsageDetailsSelector } from './customer.convergent.biller.selectors';
import { getFormattedServiceAttributeValue } from './services.list.selectors.helper';
import { unitsOfMeasureSelector } from './shared.entitlements.selectors';
import { getUnitOfMeasure } from './shared.entitlements.selectors.helper';
var EMPTY_ARRAY = [];
var EMPTY_OBJECT = {};
var findItemInCollectionForKeyMatchingValue = function findItemInCollectionForKeyMatchingValue(collection, key, value) {
  return collection.find(function (unit) {
    return unit[key] === value;
  });
};
var getNameForMetadataCode = function getNameForMetadataCode(code, metadataCodes) {
  var metadataItemForCode = findItemInCollectionForKeyMatchingValue(metadataCodes, 'Value', code);
  return metadataItemForCode ? metadataItemForCode.Name : '';
};
var usageDetailsRecoverableUIStateSelector = function usageDetailsRecoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.usageDetails;
};
var UsageDetailsStateSelector = function UsageDetailsStateSelector(state) {
  return state.customercare.customer.selectedCustomer && state.customercare.customer.selectedCustomer.usageDetails || EMPTY_OBJECT;
};
var GroupedEntitlementsSelector = createImmutableSelector([UsageDetailsStateSelector], function (usageDetailsStore) {
  return usageDetailsStore.groupedEntitlements || EMPTY_ARRAY;
});
export var ServiceEntitlementsSelector = createImmutableSelector([UsageDetailsStateSelector], function (usageDetailsStore) {
  return usageDetailsStore.serviceEntitlements || EMPTY_ARRAY;
});
export var SelectedNetworkEventTypeFiltersSelector = createSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.networkTypeFilter;
});
export var SelectedNetworkEventSubTypeFiltersSelector = createImmutableSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.eventSubTypeFilter;
});
export var SelectedNetworkEventTypeFiltersFormatSelector = createSelector([SelectedNetworkEventTypeFiltersSelector], function (networkEventType) {
  return networkEventType ? networkEventType.map(function (eventType) {
    return eventType.value;
  }) : null;
});
export var UsageDetailsSelectedStartDateSelector = createSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.startDate;
});
export var UsageDetailsSelectedEndDateSelector = createSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.endDate;
});
export var UsageDetailsSearchEntitlementsDatePickerSelector = createImmutableSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.searchEntitlementsDatePicker;
});
export var UsageDetailsSearchEntitlementsIncludeExpiredSelector = createImmutableSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.includeExpiredEntitlements;
});
export var UsageDetailsSelectedCallCategoriesSelector = createImmutableSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.callCategories;
});
export var UsageDetailsSearchedEntitlementNameSelector = createSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.entitlementName;
});
export var UsageDetailsTotalAmountSelector = createSelector([usageDetailsRecoverableUIStateSelector, CurrentAccountCurrencyCodeSelector], function (usageDetails, accountCurrencyCode) {
  if (usageDetails && usageDetails.totalAmount && (accountCurrencyCode || usageDetails.currencyCode)) {
    var $filter = getFilterService();
    return $filter('invCurrency')(usageDetails.totalAmount, accountCurrencyCode || usageDetails.currencyCode, false, false, true);
  }
});
export var UsageDetailsSortParamsSelector = createImmutableSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.sortParams;
});
export var IsSharedEntintitlementUsageSelector = createImmutableSelector([RouteParams], function (routeParams) {
  return !!routeParams.isShared;
});
var paginationDataSelector = createImmutableSelector([usageDetailsRecoverableUIStateSelector], function (usageDetails) {
  return usageDetails.paginationData;
});
export var UsageDetailsPaginationDataSelector = createImmutableSelector([paginationDataSelector, PageSizePreferenceSelector], function (paginationData, defaultPageSize) {
  if (!paginationData.pageSize) {
    return Object.assign({}, paginationData, {
      pageSize: defaultPageSize
    });
  }
  return paginationData;
});
export var SelectedEntitlementDetailsSelector = createImmutableSelector([usageDetailsRecoverableUIStateSelector, RouteParams], function (usageDetails, routeParams) {
  return usageDetails.selectedEntitlementDetails && usageDetails.selectedEntitlementDetails.entitlementIndex !== undefined ? usageDetails.selectedEntitlementDetails : {
    entitlementIndex: +routeParams.entitlementIndex || 0,
    entitlementId: routeParams.entitlementId,
    balanceIterationId: routeParams.balanceIterationId
  };
});
export var AccountUsageDetailsCurrentServiceSelector = createImmutableSelector([CurrentAccountSummarySelector, RouteParams], function (accountSummary, routeParams) {
  return accountSummary && (accountSummary.Services || EMPTY_ARRAY).find(function (service) {
    return service.ServiceIdentifier.Value === routeParams.serviceIdentifierValue;
  }) || EMPTY_OBJECT;
});
export var SelectedGroupedEntitlementSelector = createImmutableSelector([RouteParams, GroupedEntitlementsSelector], function (routeParams, groupedEntitlements) {
  var response;
  if (routeParams.isShared && routeParams.groupCode) {
    response = (groupedEntitlements || []).find(function (entitlement) {
      return entitlement.EntitlementIdentifier.EntitlementId === routeParams.entitlementId;
    });
  }
  return response;
});
export var SharedEntitlementsSelector = createImmutableSelector([UsageDetailsStateSelector], function (usageDetailsStore) {
  return usageDetailsStore.sharedEntitlements || EMPTY_ARRAY;
});
export var SelectedSharedEntitlementSelector = createImmutableSelector([RouteParams, SharedEntitlementsSelector], function (routeParams, sharedEntitlements) {
  var sharedEntitlement;
  if (routeParams.isShared) {
    sharedEntitlement = (sharedEntitlements || []).find(function (entitlement) {
      return entitlement.EntitlementIdentifier.EntitlementId === routeParams.entitlementId;
    });
  }
  return sharedEntitlement || {};
});
var EntitlementBalancesFromUsageDetails = createImmutableSelector([SelectedGroupedEntitlementSelector, SelectedSharedEntitlementSelector], function (groupedEntitlement, sharedEntitlement) {
  return groupedEntitlement ? [groupedEntitlement] : [sharedEntitlement] || EMPTY_ARRAY;
});
export var IsLoadingEntitlementsSelector = createImmutableSelector([UsageDetailsStateSelector], function (usageDetailsStore) {
  return usageDetailsStore.isLoading;
});
export var AccountUsageDetailsEntitlementsSelector = createImmutableSelector([IsSharedEntintitlementUsageSelector, ServiceEntitlementsSelector, EntitlementBalancesFromUsageDetails, unitsOfMeasureSelector, CurrentBusinessUnitTimeZoneOffsetSelector], function (isSharedUsage, serviceEntitlements, entitlementsFromUsageDetails, unitsOfMeasure, timeZoneOffset) {
  var entitlements = (isSharedUsage ? entitlementsFromUsageDetails : serviceEntitlements).reduce(function (entitlements, entitlement, index) {
    if (entitlement.ActivationStatus !== SERVICE_ENTITLEMENTS_ACTIVATION_STATUS.ENTITLEMENT_INACTIVE) {
      entitlements.push(Object.assign({}, entitlement, {
        index: index,
        unitOfMeasure: getUnitOfMeasure(entitlement.BalanceUnitCode, unitsOfMeasure),
        activationDate: entitlement.ActivationDate ? specifyTimezoneWithoutBrowserOffset(entitlement.ActivationDate, timeZoneOffset) : null,
        expirationDate: entitlement.ActivationDate ? specifyTimezoneWithoutBrowserOffset(entitlement.ExpirationDate, timeZoneOffset) : null,
        selectAll: false
      }));
    }
    return entitlements;
  }, []);
  if (entitlements.length) {
    var entitlementsAll = entitlements.asMutable({
      deep: true
    });
    entitlementsAll.push({
      selectAll: true
    });
    return entitlementsAll;
  } else {
    return entitlements;
  }
});
export var SelectedEntitlementSelector = createImmutableSelector([ServiceEntitlementsSelector, SharedEntitlementsSelector, SelectedEntitlementDetailsSelector, IsSharedEntintitlementUsageSelector], function (serviceEntitlements, sharedEntitlements, selectedEntitlementDetails, isShared) {
  var entitlements = isShared ? sharedEntitlements : serviceEntitlements;
  var selectedEntitlement = entitlements.find(function (entitlement) {
    return selectedEntitlementDetails.entitlementId && entitlement.EntitlementIdentifier.EntitlementId === selectedEntitlementDetails.entitlementId && entitlement.EntitlementIdentifier.BalanceIterationId === selectedEntitlementDetails.balanceIterationId;
  });
  return selectedEntitlement || entitlements[selectedEntitlementDetails.entitlementIndex] || EMPTY_OBJECT;
});
export var FormattedServiceAttributeValueSelector = createImmutableSelector([RouteParams, MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute), MetadataCodeTypeDictionarySelector(CODES.RegularExpression)], function (routeParams, serviceAttributes, regularExpressions) {
  return getFormattedServiceAttributeValue(routeParams.serviceAttributeId, routeParams.serviceIdentifierValue, serviceAttributes, regularExpressions);
});
export var UsageDetailsPageTitleSelector = createSelector([RouteParams, IsSharedEntintitlementUsageSelector, SelectedEntitlementSelector, FormattedServiceAttributeValueSelector], function (routeParams, isShared, selectedEntitlement, formattedServiceAttributeValue) {
  return isShared ? selectedEntitlement.EntitlementName : "".concat(formattedServiceAttributeValue, " ").concat(routeParams.serviceIdentifierFriendlyName || '');
});
var getCallDirection = function getCallDirection(callDirection) {
  switch (callDirection) {
    case USAGE_CALL_DIRECTION.OUTGOING:
      return localeKeys.USAGE_DETAILS.TABLE_CONTENT.DIRECTION_OUTGOING;
    case USAGE_CALL_DIRECTION.INCOMING:
      return localeKeys.USAGE_DETAILS.TABLE_CONTENT.DIRECTION_INCOMING;
    case USAGE_CALL_DIRECTION.CALL_FORWARDING:
      return localeKeys.USAGE_DETAILS.TABLE_CONTENT.CALL_FORWARDING;
    default:
      return undefined;
  }
};
var specifyTimezoneWithoutBrowserOffset = function specifyTimezoneWithoutBrowserOffset(dateObject, timezone) {
  var buTimezoneOffsetMinutes = moment.duration(timezone).asMinutes();
  return moment(dateObject).utcOffset(buTimezoneOffsetMinutes).format('MM/DD/YY hh:mm A');
};
export var AccountUsageDetailsTableDataSelector = createSelector([IsFetchingAccountUsageDetailsSelector, MetadataCodeLoadedSelector(CODES.NetworkEventType), MetadataCodeLoadedSelector(CODES.NetworkEventSubType), MetadataCodeLoadedSelector(CODES.UnitsOfMeasure), MetadataCodeLoadedSelector(CODES.UsageUnitConversion), MetadataCodeTypeSelector(CODES.NetworkEventType), MetadataCodeTypeSelector(CODES.NetworkEventSubType), MetadataCodeTypeDictionarySelector(CODES.UnitsOfMeasure), MetadataCodeTypeDictionarySelector(CODES.UsageUnitConversion), ConvergentBillerAccountUsageDetailsByServiceIdSelector, CurrentAccountCurrencyCodeSelector, CurrentBusinessUnitTimeZoneOffsetSelector], function (isFetchingAccountUsageDetails, networkEventsLoaded, networkEventSubTypesLoaded, unitsOfMeasureLoaded, usageUnitConversionLoaded, networkEvents, networkEventSubTypes, unitsOfMeasure, usageUnitConversion, accountUsageDetailsForServiceId, accountCurrencyCode, TimeZoneOffset) {
  var $filter = getFilterService();
  var formatView = function formatView(usageDetailItem) {
    var USAGE_DIRECTION_KEY = getCallDirection(usageDetailItem.CallDirection);
    var mappedTriggers = pathOr([], ['TriggerEvents'], usageDetailItem).map(function (trigger) {
      return {
        title: trigger.TriggerName,
        id: trigger.TriggerBillerRuleConfigurationId
      };
    });
    var usrConvertedActualBalance = usageDetailItem.CallDirection ? convertUnitsUserFriendlyIntoSmallerDescriptiveUnits(usageDetailItem.ActualUsageAmount, +usageDetailItem.UnitOfMeasureCode, usageUnitConversion) : convertUnitsUserFriendly(usageDetailItem.ActualUsageAmount, +usageDetailItem.UnitOfMeasureCode, usageUnitConversion, BYTE_UNIT_CODE, getAllUnitsInTheUnitCategory(+usageDetailItem.ConversionUnitOfMeasureCode, usageUnitConversion));
    var usrConvertedBilledBalance = usageDetailItem.CallDirection ? convertUnitsUserFriendlyIntoSmallerDescriptiveUnits(usageDetailItem.UsageAmount, +usageDetailItem.UnitOfMeasureCode, usageUnitConversion) : convertUnitsUserFriendly(usageDetailItem.UsageAmount, +usageDetailItem.UnitOfMeasureCode, usageUnitConversion, BYTE_UNIT_CODE, getAllUnitsInTheUnitCategory(+usageDetailItem.ConversionUnitOfMeasureCode, usageUnitConversion));
    var eventOffsetLocal = TimeZoneOffset ? i18n.translate(localeKeys.UTC) + TimeZoneOffset.toString() : '';
    return {
      actionType: USAGE_DETAILS_ACTION_TYPES()[usageDetailItem.ActionType],
      callCategory: usageDetailItem.CallCategory,
      charge: $filter('invCurrency')(usageDetailItem.ChargeAmount, accountUsageDetailsForServiceId.CurrencyCode || accountCurrencyCode, false, true, true),
      dateTime: usageDetailItem.EventStartTime ? specifyTimezoneWithoutBrowserOffset(usageDetailItem.EventStartTime, TimeZoneOffset) : i18n.translate(localeKeys.NOT_APPLICABLE),
      dateTimeLocalWithOffset: usageDetailItem.EventStartTime ? "".concat(specifyTimezoneWithoutBrowserOffset(usageDetailItem.EventStartTime, TimeZoneOffset), " ").concat(eventOffsetLocal) : i18n.translate(localeKeys.NOT_APPLICABLE),
      direction: USAGE_DIRECTION_KEY && i18n.translate(USAGE_DIRECTION_KEY),
      entitlementName: usageDetailItem.EntitlementName,
      eventSource: usageDetailItem.EventSource,
      networkEventSubTypeName: getNameForMetadataCode(usageDetailItem.NetworkEventSubTypeCode, networkEventSubTypes) || i18n.translate(localeKeys.USAGE_DETAILS.TABLE_CONTENT.UNKNOWN_NETWORK_EVENT),
      networkEventTypeName: getNameForMetadataCode(usageDetailItem.NetworkEventTypeCode, networkEvents) || i18n.translate(localeKeys.USAGE_DETAILS.TABLE_CONTENT.UNKNOWN_NETWORK_EVENT),
      processDate: usageDetailItem.ProcessDate ? specifyTimezoneWithoutBrowserOffset(usageDetailItem.ProcessDate, TimeZoneOffset) : i18n.translate(localeKeys.NOT_APPLICABLE),
      receiver: usageDetailItem.Receiver,
      receiverWithLocation: "".concat(usageDetailItem.Receiver ? usageDetailItem.Receiver : '') + " ".concat(usageDetailItem.ToLocation ? "(".concat(usageDetailItem.ToLocation, ")") : ''),
      sender: usageDetailItem.Sender,
      senderWithLocation: "".concat(usageDetailItem.Sender ? usageDetailItem.Sender : '') + " ".concat(usageDetailItem.FromLocation ? "(".concat(usageDetailItem.FromLocation, ")") : ''),
      triggers: mappedTriggers,
      uomUnit: usageDetailItem.UnitDisplayName || null,
      uomUnitActualDisplayName: usageDetailItem.UnitDisplayName ? "".concat(usageDetailItem.ActualUsageAmount, " ").concat(usageDetailItem.UnitDisplayName) : null,
      uomUnitBilledDisplayName: usageDetailItem.UnitDisplayName ? "".concat(usageDetailItem.UsageAmount, " ").concat(usageDetailItem.UnitDisplayName) : null,
      usage_actual: usageDetailItem.UnitOfMeasureCode === UNIT_OF_MEASURES.CURRENCY ? {
        value: usageDetailItem.ActualUsageAmount,
        unitOfMeasure: +usageDetailItem.UnitOfMeasureCode
      } : usrConvertedActualBalance,
      usage_billed: usageDetailItem.UnitOfMeasureCode === UNIT_OF_MEASURES.CURRENCY ? {
        value: usageDetailItem.UsageAmount,
        unitOfMeasure: +usageDetailItem.UnitOfMeasureCode
      } : usrConvertedBilledBalance,
      financial_impact: usageDetailItem.FinancialImplication
    };
  };
  var necessaryDataHasBeenLoaded = !isFetchingAccountUsageDetails && networkEventsLoaded && networkEventSubTypesLoaded && unitsOfMeasureLoaded && usageUnitConversionLoaded && networkEvents && networkEventSubTypes && unitsOfMeasure;
  var accountUsageDetailsHasUsageItems = accountUsageDetailsForServiceId && accountUsageDetailsForServiceId.UsageItems;
  return necessaryDataHasBeenLoaded && accountUsageDetailsHasUsageItems && accountUsageDetailsForServiceId ? accountUsageDetailsForServiceId.UsageItems.map(formatView).asMutable({
    deep: true
  }) : EMPTY_ARRAY;
});